import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaHeart, FaRegHeart, FaMapMarkerAlt } from "react-icons/fa";
import { FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";

export default function TopRatedHotels({ limit }) {
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [wishlist, setWishlist] = useState([]);
    const navigate = useNavigate();

    // Load wishlist from localStorage
    useEffect(() => {
        const storedWishlist = JSON.parse(localStorage.getItem("wishlist")) || [];
        setWishlist(storedWishlist);
    }, []);

    // Save wishlist
    const updateWishlist = (updated) => {
        setWishlist(updated);
        localStorage.setItem("wishlist", JSON.stringify(updated));
    };

    // Toggle wishlist
    const toggleWishlist = (hotel) => {
        const isLoggedIn = true; // replace later with auth

        if (!isLoggedIn) {
            alert("Please login to add to wishlist");
            return;
        }

        const exists = wishlist.find(item => item.id === hotel.id);

        let updated;
        if (exists) {
            updated = wishlist.filter(item => item.id !== hotel.id);
        } else {
            updated = [...wishlist, hotel];
        }

        updateWishlist(updated);
    };

    const isWishlisted = (id) => {
        return wishlist.some(item => item.id === id);
    };

    useEffect(() => {
        const fetchTopHotels = async () => {
            try {
                const res = await axios.get("https://hotel.southtravels.com/api/top-rated-hotels");
                setHotels(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchTopHotels();
    }, []);

    // ⭐ render stars
    const renderStars = (rating) => {
        const stars = [];
        const safeRating = rating || 0;

        for (let i = 0; i < 5; i++) {
            if (i < Math.floor(safeRating)) {
                stars.push(<FaStar key={i} />);
            } else if (i === Math.floor(safeRating) && safeRating % 1 >= 0.5) {
                stars.push(<FaStarHalfAlt key={i} />);
            } else {
                stars.push(<FaRegStar key={i} className="text-gray-300" />);
            }
        }

        return stars;
    };

    return (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8 sm:mb-10">
                <div>
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800">
                        Top Rated Hotels
                    </h2>
                    <p className="text-sm sm:text-base text-gray-500 mt-2">
                        Discover the highest rated stays for your next trip
                    </p>
                </div>

                {/* Desktop Button */}
                <button
                    onClick={() => navigate("/hotels")}
                    className="hidden sm:inline-flex bg-gray-900 text-white px-5 py-3 rounded-lg hover:bg-gray-800 transition text-sm sm:text-base"
                >
                    View All
                </button>
            </div>

            {/* Loading */}
            {loading ? (
                <div className="flex justify-center py-10">
                    <div className="animate-spin h-10 w-10 border-4 border-teal-500 border-t-transparent rounded-full"></div>
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
                        {hotels.slice(0, limit).map((hotel) => (
                            <div
                                key={hotel.id}
                                className="group bg-white rounded-2xl overflow-hidden shadow-2xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
                            >
                                {/* Image */}
                                <div
                                    className="relative h-56 sm:h-60 md:h-64 w-full overflow-hidden cursor-pointer"
                                    onClick={() => navigate(`/hotels/${hotel.slug}`)}
                                >
                                    <img
                                        src={`https://uaediscountedhotels.com/uploads/hotels/${hotel.image}`}
                                        alt={hotel.name}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />

                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>

                                    {/* Wishlist Button */}
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleWishlist(hotel);
                                        }}
                                        className="absolute top-3 right-3 bg-white/90 p-2 rounded-full shadow hover:scale-110 transition"
                                    >
                                        {isWishlisted(hotel.id) ? (
                                            <FaHeart className="text-red-500 text-sm sm:text-base" />
                                        ) : (
                                            <FaRegHeart className="text-gray-600 text-sm sm:text-base" />
                                        )}
                                    </button>

                                    {/* Rating Badge */}
                                    <div className="absolute top-3 left-3 text-yellow-500 bg-white/90 text-gray-800 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold shadow flex items-center gap-1">
                                        {renderStars(hotel.rating)}
                                        <span className="ml-1 text-gray-700 text-[10px] sm:text-xs font-medium">
                                            ({hotel.rating})
                                        </span>
                                    </div>

                                    {/* Price */}
                                    <div className="absolute bottom-3 right-3 bg-teal-500 text-white px-3 py-1 rounded-full text-xs sm:text-sm font-bold shadow">
                                        {Math.round(hotel.price_per_night)} AED / night
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-4 sm:p-5">
                                    <h3
                                        onClick={() => navigate(`/hotels/${hotel.slug}`)}
                                        className="text-lg sm:text-xl font-semibold text-gray-800 group-hover:text-teal-600 transition cursor-pointer hover:underline line-clamp-1"
                                    >
                                        {hotel.name}
                                    </h3>

                                    <p className="text-sm text-gray-500 mt-1 flex items-center gap-1 line-clamp-1">
                                        <FaMapMarkerAlt className="text-blue-900 shrink-0" />
                                        {hotel.location}
                                    </p>

                                    <div className="border-t my-3"></div>

                                    <p className="text-sm text-gray-600 line-clamp-2 sm:line-clamp-3 min-h-[40px] sm:min-h-[60px]">
                                        {hotel.short_description.replace(/<[^>]*>/g, "")}
                                    </p>

                                    <button
                                        onClick={() => navigate(`/hotels/${hotel.slug}`)}
                                        className="mt-5 w-full bg-gradient-to-r from-teal-500 to-blue-500 text-white py-2.5 rounded-lg font-medium hover:from-teal-600 hover:to-blue-600 transition text-sm sm:text-base"
                                    >
                                        View Details
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Mobile Button */}
                    <div className="mt-8 sm:hidden">
                        <button
                            onClick={() => navigate("/hotels")}
                            className="w-full bg-gray-900 text-white px-5 py-3 rounded-lg hover:bg-gray-800 transition text-sm"
                        >
                            View All
                        </button>
                    </div>
                </>
            )}
        </section>
    );
}